import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux paiements
 * 
 */
class PaymentService {
    /**
     * Effectuer un paiement pour une vente spécifique
     * @param {Object} paymentData Données du paiement
     * @param {Number} paymentData.sale_id ID de la vente (requis)
     * @param {String} paymentData.payment_type Type de paiement (requis): cash, card, mobile_money, bank_transfer, check, wallet
     * @param {String} [paymentData.payment_date] Date du paiement (optionnel, format: YYYY-MM-DD)
     * @param {Number} [paymentData.cash_register_id] ID de la caisse enregistreuse (requis sauf si payment_type = 'wallet')
     * @param {String} [paymentData.notes] Notes additionnelles (optionnel, max: 500 caractères)
     * @returns {Promise} Réponse de l'API avec le paiement créé
     * @note Pour les paiements par portefeuille (wallet), le cash_register_id n'est pas requis.
     *       Le portefeuille du client associé à la vente sera automatiquement débité.
     */
    makePayment(paymentData) {
        return ApiService.post('/payments/make-payment', paymentData);
    }

    /**
     * Effectuer des paiements en lot pour plusieurs ventes d'un client
     * @param {Object} bulkPaymentData Données des paiements en lot
     * @returns {Promise} Réponse de l'API avec les paiements traités
     */
    bulkPayments(bulkPaymentData) {
        return ApiService.post('/payments/bulk-payments', bulkPaymentData);
    }

    /**
     * Récupérer l'historique des paiements d'un client
     * @param {Number} customerId ID du client
     * @param {Object} params Paramètres additionnels (pagination)
     * @returns {Promise} Réponse de l'API avec l'historique des paiements
     */
    getCustomerPayments(customerId, params = {}) {
        return ApiService.get(`/payments/customer/${customerId}`, params);
    }

    /**
     * Récupérer les ventes impayées d'un client
     * @param {Number} customerId ID du client
     * @returns {Promise} Réponse de l'API avec les ventes impayées
     */
    getUnpaidSales(customerId) {
        return ApiService.get(`/payments/customer/${customerId}/unpaid`);
    }

    /**
     * Télécharger le reçu de paiement pour un paiement donné
     * @param {Number} paymentId ID du paiement
     * @param {Object} config Config optionnel pour le téléchargement (ex: { responseType: 'blob' })
     * @returns {Promise} Réponse de l'API avec le reçu (fichier)
     */
    downloadReceipt(paymentId, config = { responseType: 'blob' }) {
        return ApiService.post(`/payments/${paymentId}/receipt`, {}, config);
    }

    /**
     * Préparer les données pour un paiement individuel
     * @param {Number} saleId ID de la vente
     * @param {String} paymentType Type de paiement (cash, card, mobile_money, bank_transfer, check, wallet)
     * @param {Number} amount Montant du paiement
     * @param {Object} options Options additionnelles
     * @param {String} [options.paymentDate] Date du paiement
     * @param {String} [options.reference] Référence du paiement
     * @param {String} [options.notes] Notes additionnelles
     * @param {Number} [options.cashRegisterId] ID de la caisse (requis sauf si paymentType = 'wallet')
     * @returns {Object} Données formatées pour l'API
     * @note Pour les paiements par portefeuille (wallet), cashRegisterId n'est pas requis
     */
    prepareSinglePaymentData(saleId, paymentType, amount, options = {}) {
        const data = {
            sale_id: saleId,
            payment_type: paymentType,
            amount: amount,
            payment_date: options.paymentDate || null,
            reference: options.reference || null,
            notes: options.notes || null
        };

        // Ajouter cash_register_id seulement si ce n'est pas un paiement par portefeuille
        if (paymentType !== 'wallet' && options.cashRegisterId) {
            data.cash_register_id = options.cashRegisterId;
        }

        return data;
    }

    /**
     * Préparer les données pour des paiements en lot
     * @param {Number} customerId ID du client
     * @param {String} paymentType Type de paiement
     * @param {Array} salesPayments Array des ventes avec montants
     * @param {Object} options Options additionnelles
     * @returns {Object} Données formatées pour l'API
     */
    prepareBulkPaymentData(customerId, paymentType, salesPayments, options = {}) {
        return {
            customer_id: customerId,
            payment_type: paymentType,
            payments: salesPayments.map(sp => ({
                sale_id: sp.saleId,
                amount: sp.amount
            })),
            payment_date: options.paymentDate || null,
            reference: options.reference || null,
            notes: options.notes || null
        };
    }

    async printPaymentReceipt(paymentId, open = false) {
        const response = await ApiService.post(`/payments/${paymentId}/receipt`, {}, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        if (open) {
            // Ouvrir dans un nouvel onglet pour prévisualisation
            window.open(url, '_blank');
        } else {
            // Télécharger directement
            const link = document.createElement('a');
            link.href = url;
            link.download = `recu-de-paiement-${paymentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        // Nettoyer l'URL après un délai pour permettre l'ouverture
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    }

    /**
     * Calculer le total des paiements en lot
     * @param {Array} salesPayments Array des ventes avec montants
     * @returns {Number} Montant total
     */
    calculateBulkTotal(salesPayments) {
        return salesPayments.reduce((total, sp) => total + (sp.amount || 0), 0);
    }

    /**
     * Valider les données de paiement côté client
     * @param {Object} paymentData Données du paiement
     * @returns {Object} Résultat de la validation
     */
    validatePaymentData(paymentData) {
        const errors = [];

        // Validation du montant
        if (!paymentData.amount || paymentData.amount <= 0) {
            errors.push('Le montant doit être supérieur à 0');
        }

        // Validation du type de paiement
        const validPaymentTypes = ['cash', 'card', 'wave', 'orange_money', 'mobile_money', 'bank_transfer', 'check', 'wallet'];
        if (!paymentData.payment_type || !validPaymentTypes.includes(paymentData.payment_type)) {
            errors.push('Type de paiement invalide');
        }

        // Validation de la vente (pour paiement individuel)
        if (paymentData.sale_id && (!Number.isInteger(paymentData.sale_id) || paymentData.sale_id <= 0)) {
            errors.push('ID de vente invalide');
        }

        // Validation de la caisse (requis sauf pour les paiements par portefeuille)
        if (paymentData.payment_type !== 'wallet' && !paymentData.cash_register_id) {
            errors.push('ID de caisse enregistreuse requis pour ce type de paiement');
        }

        // Validation du client (pour paiements en lot)
        if (paymentData.customer_id && (!Number.isInteger(paymentData.customer_id) || paymentData.customer_id <= 0)) {
            errors.push('ID de client invalide');
        }

        // Validation de la date de paiement
        if (paymentData.payment_date) {
            const paymentDate = new Date(paymentData.payment_date);
            if (isNaN(paymentDate.getTime())) {
                errors.push('Date de paiement invalide');
            }
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Formater les types de paiement pour l'affichage
     * @param {String} paymentType Type de paiement
     * @returns {String} Libellé formaté
     */
    formatPaymentType(paymentType) {
        const paymentTypes = {
            'cash': 'Espèces',
            'card': 'Carte bancaire',
            'wave': 'Wave',
            'orange_money': 'Orange Money',
            'mobile_money': 'Mobile Money',
            'bank_transfer': 'Virement bancaire',
            'check': 'Chèque',
            'wallet': 'Portefeuille'
        };

        return paymentTypes[paymentType] || paymentType;
    }

    /**
     * Obtenir la liste des types de paiement disponibles
     * @returns {Array} Liste des types de paiement
     */
    getPaymentTypes() {
        return [
            { id: 'cash', name: 'Espèces', icon: 'cash' },
            { id: 'card', name: 'Carte bancaire', icon: 'card' },
            { id: 'wave', name: 'Wave', icon: 'mobile' },
            { id: 'orange_money', name: 'Orange Money', icon: 'mobile' },
            { id: 'mobile_money', name: 'Mobile Money', icon: 'mobile' },
            { id: 'bank_transfer', name: 'Virement bancaire', icon: 'bank' },
            { id: 'check', name: 'Chèque', icon: 'check' },
            { id: 'wallet', name: 'Portefeuille', icon: 'wallet' }
        ];
    }

    /**
     * Formater un montant en devise locale
     * @param {Number} amount Montant à formater
     * @returns {String} Montant formaté
     */
    formatCurrency(amount) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0
        }).format(amount || 0);
    }

    /**
     * Formater une date de paiement
     * @param {String} dateString Date à formater
     * @returns {String} Date formatée
     */
    formatPaymentDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Valider qu'un montant correspond au total d'une vente
     * @param {Number} paymentAmount Montant du paiement
     * @param {Number} saleTotal Total de la vente
     * @returns {Boolean} True si les montants correspondent
     */
    validatePaymentAmount(paymentAmount, saleTotal) {
        return Math.abs(paymentAmount - saleTotal) <= 0.01;
    }

    /**
     * Grouper les paiements par date
     * @param {Array} payments Liste des paiements
     * @returns {Object} Paiements groupés par date
     */
    groupPaymentsByDate(payments) {
        return payments.reduce((groups, payment) => {
            const date = payment.payment_date.split('T')[0]; // YYYY-MM-DD
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(payment);
            return groups;
        }, {});
    }

    /**
     * Calculer les statistiques de paiement pour un client
     * @param {Array} payments Liste des paiements
     * @returns {Object} Statistiques calculées
     */
    calculatePaymentStats(payments) {
        const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);
        const paymentsByType = payments.reduce((stats, payment) => {
            stats[payment.payment_type] = (stats[payment.payment_type] || 0) + payment.amount;
            return stats;
        }, {});

        return {
            totalAmount,
            totalCount: payments.length,
            paymentsByType,
            averagePayment: payments.length > 0 ? totalAmount / payments.length : 0
        };
    }
}

export default new PaymentService();
